import {
  Children,
  ForwardRefExoticComponent,
  MouseEventHandler,
  ReactNode,
  RefAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  UIEvent as ReactUIEvent,
} from 'react';

import { useResizeEffect } from '../../hooks/useResizeEffect';
import { useModifiers } from '../../styles/hooks/useModifiers';

import styles from './Swiper.module.css';

export interface SwiperProps {
  children: ReactNode;
  className?: string;
  onActiveChange?: (index: number) => void;
  dots?: boolean;
  slideContainerClassName?: string;
}

export interface SwiperHandle {
  goTo: (index: number | 'next' | 'previous') => void;
}

const THRESHOLD = 10;

// TO-DO fix mouse drop issue, track active index by center of slide, not beginning
export const Swiper: ForwardRefExoticComponent<SwiperProps & RefAttributes<SwiperHandle>> = forwardRef<
  SwiperHandle,
  SwiperProps
>(function SwipeScroll(
  { children, className = '', onActiveChange, dots = false, slideContainerClassName }: SwiperProps,
  ref
) {
  const [scrollLeft, setScrollLeft] = useState<number>(0);

  const [activeIndex, setActiveIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(3);
  const [styleChangeTimeout, setStyleChangeTimeout] = useState(setTimeout(() => {}));

  const [moveObject, setMoveObject] = useState({
    startScrollLeft: 0,
    isStartMove: false,
    isStopClick: false,
    startX: 0,
    endX: 0,
  });

  const [slideWidth, setSlideWidth] = useState(0);

  const throttleScroll = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const mods = useModifiers(
    styles,
    'swiper__container',
    {},
    {
      withBaseClass: true,
    }
  );

  useResizeEffect(
    useCallback(() => {
      setSlideWidth(containerRef?.current?.children[0].getBoundingClientRect().width ?? 0);
    }, [])
  );

  const handleScroll = (event: ReactUIEvent<HTMLDivElement, UIEvent>): void => {
    const target = event.currentTarget;

    if (throttleScroll.current) {
      return;
    }
    throttleScroll.current = true;

    setTimeout(() => {
      const overscrollStart = target.scrollLeft;
      setScrollLeft(overscrollStart);

      throttleScroll.current = false;
    }, 100);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const slideOffsets = Array.apply(null, Array(slideCount)).map((_, index) => {
      return index * slideWidth + slideWidth / 2;
    });

    const activeSlide = slideOffsets.findIndex((slideOffset) => {
      return Math.abs(slideOffset - (scrollLeft + slideWidth / 2)) < slideWidth / 2;
    });
    setActiveIndex(activeSlide < 0 ? 0 : activeSlide);
  }, [scrollLeft, slideWidth, slideCount]);

  useEffect(() => {
    setSlideCount(Children.toArray(children).length);
  }, [children]);

  const goTo = useCallback(
    (index: number | 'next' | 'previous'): void => {
      if (!containerRef.current) return;
      const swiperElement = containerRef.current;

      const scrollTo = (index: number): void => {
        swiperElement.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
      };

      if (index === 'next' || index === 'previous') {
        if (index === 'next' && activeIndex < slideCount - 1) {
          scrollTo(activeIndex + 1);
        }
        if (index === 'previous' && activeIndex > 0) {
          scrollTo(activeIndex - 1);
        }
      } else if (index >= 0) {
        scrollTo(index);
      }
    },
    [activeIndex, slideCount, slideWidth]
  );

  const getScrollLength = (): number => {
    return moveObject.endX === 0 ? 0 : Math.round(moveObject.startX - moveObject.endX);
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (moveObject.isStopClick) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    const container = containerRef.current;

    if (!container) return;

    clearTimeout(styleChangeTimeout);
    container.style.scrollSnapType = 'none';

    setMoveObject((prev) => ({
      ...prev,
      isStartMove: true,
      startX: event.clientX,
      startScrollLeft: scrollLeft,
    }));
  };

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = (event) => {
    if (!moveObject.isStartMove) return;

    const container = containerRef.current;
    if (!container) return;

    const scrollLength = getScrollLength();

    if (Math.abs(scrollLength) > THRESHOLD) {
      container.scrollTo({ left: moveObject.startScrollLeft + scrollLength });
    }

    setMoveObject((prev) => ({
      ...prev,
      endX: event.clientX,
      endY: event.clientY,
    }));
  };

  const handleMouseUp: MouseEventHandler<HTMLDivElement> = () => {
    const container = containerRef.current;

    if (!container) return;

    setTimeout(() => {
      const moveX = moveObject.endX === 0 ? 0 : moveObject.startX - moveObject.endX;
      if (throttleScroll.current && Math.abs(moveX) > THRESHOLD) {
        goTo(activeIndex);
      } else if (moveX > THRESHOLD) {
        goTo('next');
      } else if (moveX < -THRESHOLD) {
        goTo('previous');
      }
    }, 150);

    setMoveObject({
      startX: 0,
      endX: 0,
      isStartMove: false,
      isStopClick: false,
      startScrollLeft: 0,
    });

    setStyleChangeTimeout(setTimeout(() => (container.style.scrollSnapType = 'x mandatory'), 1000));
  };

  const newChildren = useMemo(() => {
    return Children.map(children, (child) => <div className={styles.swiper__item}>{child}</div>);
  }, [children]);

  const renderDots = useMemo((): ReactNode => {
    if (!dots) {
      return;
    }
    return (
      <div className={styles.swiperDotsWrapper}>
        <ul className={styles.swiperDots}>
          {Children.map(children, (_, index: number): ReactNode => {
            return (
              <li key={index}>
                <button
                  key={'dot' + index}
                  className={
                    styles.swiperDotsItem +
                    ' ' +
                    (index === activeIndex ? styles.swiperDotBtnActive : '') +
                    ' ' +
                    (index + 1 === activeIndex || index - 1 === activeIndex ? styles.swiperDotNeighbor : '')
                  }
                  tabIndex={-1}
                  data-index={index}
                  onClick={() => goTo(index)}
                >
                  <span className={styles.swiperDotInner}></span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }, [goTo, children, activeIndex, dots]);

  useImperativeHandle(
    ref,
    () => ({
      goTo(index: number | 'next' | 'previous'): void {
        goTo(index);
      },
    }),
    [goTo]
  );

  useEffect(() => {
    if (onActiveChange) {
      onActiveChange(activeIndex);
    }
  }, [activeIndex, onActiveChange]);

  return (
    <div
      className={`${className} ${styles.swiper__wrapper}`}
      onClickCapture={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
      onMouseUp={handleMouseUp}
    >
      <div className={`${mods} ${slideContainerClassName}`} onScroll={handleScroll} ref={containerRef}>
        {newChildren}
      </div>
      {renderDots}
    </div>
  );
});
